// YERİ: src/app/domains/website/sections/mkt-legal.section.ts
//
// Hukuki metin bölümü — gizlilik politikası, kullanım şartları, veri silme.
//
// ══ NEDEN AYRI BİR BLOK TİPİ ══════════════════════════════════════════════
// Mevcut mkt-* bloklarının hepsi PAZARLAMA düzeni: hero, özellik ızgarası,
// fiyat tablosu. Hukuki metin bunların hiçbirine benzemez — numaralı başlıklar,
// uzun paragraflar, tanım listeleri ve "son güncelleme" damgası ister.
// Bunu mkt-feature-split'e sığdırmak, avukat bir cümle değiştirdiğinde
// pazarlama şemasıyla boğuşmak demek olurdu.
//
// ══ NEDEN CMS BLOĞU, STATİK BİLEŞEN DEĞİL ═════════════════════════════════
// Hukuki metin ürünün EN ÇOK DEĞİŞEN içeriğidir: yeni bir alt-işleyici, yeni
// bir yargı yetkisi, avukat düzeltmesi. Statik bileşen olsaydı her virgül bir
// deploy isterdi. Blok olarak CMS'te durunca Mustafa admin panelinden
// düzenler ve mevcut çeviri hattı 10 dile taşır.
//
// ══ OKUNABİLİRLİK ═════════════════════════════════════════════════════════
// Ölçü 68ch'te tutuluyor. Hukuki metin zaten okunmuyor; satırı ekran boyunca
// uzatmak okunmama oranını artırır. Bu süs değil, uyum meselesi: PIPEDA
// bildirimin "anlaşılabilir" olmasını bekliyor.

import { Component, computed, input, inject } from '@angular/core';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { MktReveal } from './mkt-motion';
import { MktChromeText } from '../core/mkt-chrome-text';

interface LegalSection {
  heading?: string;
  /** Paragraflar — satır sonlarıyla ayrılır. */
  body?: string;
  /** Madde işaretli liste — satır sonlarıyla ayrılır. */
  bullets?: string;
}

@Component({
  selector: 'mkt-legal',
  standalone: true,
  imports: [MktReveal],
  host: { class: 'block' },
  template: `
    <div class="mx-auto max-w-4xl px-5 py-20 sm:px-8 lg:py-28">

      <header class="mb-12" mktReveal>
        @if (eyebrow()) {
          <p class="mkt-eyebrow">{{ eyebrow() }}</p>
        }
        @if (title()) {
          <h1 class="mt-3 text-3xl font-semibold tracking-tight text-[color:var(--mkt-ink)]
                     sm:text-4xl dark:text-white">
            {{ title() }}
          </h1>
        }
        @if (effectiveDate()) {
          <!-- Yürürlük tarihi görünür olmak ZORUNDA: hangi sürüme razı
               olunduğu ancak böyle kanıtlanır. -->
          <p class="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
            {{ effectiveLabel() || metin.t('effectiveDate') }}: {{ effectiveDate() }}
          </p>
        }
        @if (intro()) {
          @for (p of paragraphs(intro()); track $index) {
            <p class="mt-5 max-w-[68ch] text-[15px] leading-7 text-neutral-700 dark:text-neutral-300">
              {{ p }}
            </p>
          }
        }
      </header>

      <div class="flex flex-col gap-10">
        @for (s of sections(); track $index) {
          <section mktReveal>
            @if (s.heading) {
              <h2 class="text-lg font-semibold text-[color:var(--mkt-ink)] dark:text-white">
                <!-- Numaralandırma ŞABLONDA üretiliyor: içeriğe elle yazılsaydı
                     araya bölüm eklendiğinde numaralar kayardı ve metin
                     kendi iç göndermeleriyle çelişirdi. -->
                {{ $index + 1 }}. {{ s.heading }}
              </h2>
            }

            @if (s.body) {
              @for (p of paragraphs(s.body); track $index) {
                <p class="mt-3 max-w-[68ch] text-[15px] leading-7 text-neutral-700 dark:text-neutral-300">
                  {{ p }}
                </p>
              }
            }

            @if (s.bullets) {
              <ul class="mt-4 flex max-w-[68ch] list-disc flex-col gap-2 pl-5
                         text-[15px] leading-7 text-neutral-700 dark:text-neutral-300">
                @for (b of paragraphs(s.bullets); track $index) {
                  <li>{{ b }}</li>
                }
              </ul>
            }
          </section>
        }
      </div>

      @if (contact()) {
        <aside class="mt-14 rounded-2xl border border-neutral-200 bg-neutral-50 p-6
                      dark:border-neutral-700 dark:bg-neutral-800/50" mktReveal>
          @if (contactTitle()) {
            <h2 class="text-base font-semibold text-[color:var(--mkt-ink)] dark:text-white">
              {{ contactTitle() }}
            </h2>
          }
          @for (p of paragraphs(contact()); track $index) {
            <p class="mt-2 max-w-[68ch] text-[15px] leading-7 text-neutral-700 dark:text-neutral-300">
              {{ p }}
            </p>
          }
        </aside>
      }
    </div>
  `,
})
export class MktLegalSection {
  /** Vitrin kabuk metinleri — dil `/:lang` rotasindan. */
  protected metin = inject(MktChromeText);
  config = input<SiteConfig>({});

  protected eyebrow        = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title          = computed(() => cfg(this.config(), 'title', ''));
  protected effectiveLabel = computed(() => cfg(this.config(), 'effectiveLabel', ''));
  protected effectiveDate  = computed(() => cfg(this.config(), 'effectiveDate', ''));
  protected intro          = computed(() => cfg(this.config(), 'intro', ''));
  protected contactTitle   = computed(() => cfg(this.config(), 'contactTitle', ''));
  protected contact        = computed(() => cfg(this.config(), 'contact', ''));

  protected sections = computed<LegalSection[]>(() =>
    cfg<LegalSection[]>(this.config(), 'sections', []));

  /**
   * Çok satırlı CMS alanını satırlara böler.
   *
   * Ayraç `\r?\n` VE gerçek satır sonu birlikte: CMS'e yapıştırılan metin
   * kaçışlı `\n` ile de gelebiliyor, tek biçime bölmek bütün maddeleri tek
   * satıra çökertirdi.
   */
  protected paragraphs(value: string | undefined): string[] {
    if (!value) return [];
    return value
      .split(/\\n|\r?\n/)
      .map(s => s.trim())
      .filter(Boolean);
  }
}
